/**
 * Tolerant display formatters for the backend's mixed timestamp habits
 * (PX-W6.2). The px server stamps some rows in epoch millis (`createdAt`,
 * `occurredAt`, `expiresAt`) and others as ISO strings (`lastActiveAt`,
 * `joinedAt`, `readAt`); rows can also predate a field entirely. Every helper
 * renders `null` for an unusable value so callers can fall back to an i18n
 * "unknown" instead of `Invalid Date` or `NaN`.
 */

/** Coerce any backend timestamp habit into epoch millis, or null. */
export const toEpochMillis = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/** Locale date (`4 Sep 2026`); null when the value carries no usable time. */
export const formatDate = (value: unknown, locale?: string): string | null => {
  const millis = toEpochMillis(value)
  if (millis === null) return null
  return new Date(millis).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

/** Locale date + time; null when the value carries no usable time. */
export const formatDateTime = (value: unknown): string | null => {
  const millis = toEpochMillis(value)
  if (millis === null) return null
  return new Date(millis).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Relative time (`3 minutes ago` / `in 2 hours`), coarse-grained like the
 * shell's other timestamps. Falls back to `formatDateTime` beyond a week —
 * "5 months ago" carries less than the date itself.
 */
export const formatRelativeTime = (value: unknown): string | null => {
  const millis = toEpochMillis(value)
  if (millis === null) return null
  const deltaSeconds = Math.round((millis - Date.now()) / 1000)
  const abs = Math.abs(deltaSeconds)

  const table: readonly [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7]
  ]
  let amount = deltaSeconds
  let unit: Intl.RelativeTimeFormatUnit = 'second'
  for (const [nextUnit, step] of table) {
    if (abs < step) return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(
      Math.round(amount),
      unit
    )
    amount = amount / step
    unit = nextUnit
  }
  return formatDateTime(value)
}

/** First two initials for an avatar fallback (`Jules Devos` → `JD`). */
export const initialsFor = (name: string | null | undefined): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
}
